'use client'

import { RootContext } from '@/modules/contextService'
import React, {
  useEffect,
  useRef,
  useContext
} from 'react'
import Image from 'next/image'
import Link from 'next/link'
import styles from './MainFooter.module.scss'
import { useTranslations } from 'next-intl'

interface Props {
  // eslint-disable-next-line no-unused-vars
  toggleRightsMessage?: (show: boolean) => void
}

function MainFooter (props: Props) {
  const { rootData } = useContext(RootContext)
  const { user } = rootData
  const footerRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && props?.toggleRightsMessage) {
          // Perform actions when the footer comes into view
          props.toggleRightsMessage(false)
        } else {
          // Perform actions when the footer goes out of view
          if (props.toggleRightsMessage) props.toggleRightsMessage(true)
        }
      })
    }, {
      threshold: 0.1 // Adjust this value based on your requirement
    })

    if (footerRef.current) {
      observer.observe(footerRef.current)
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (footerRef.current) {
        observer.disconnect()
      }
    }
  })

  const t = useTranslations('MainFooter')

  const variables = {
    logoAlt: t('logo-alt'),
    legal: t('legal'),
    privacyPolicy: t('privacy-policy'),
    termsOfService: t('terms-of-service'),
    communityGuidelines: t('community-guidelines'),
    statementOfFaith: t('statement-of-faith'),
    account: t('account'),
    signUp: t('sign-up'),
    logIn: t('log-in'),
    platform: t('platform'),
    donate: t('donate'),
    donationFAQ: t('donation-faq'),
    home: t('home'),
    about: t('about'),
    explore: t('explore'),
    contentSuggestions: t('content-suggestions'),
    facebook: t('facebook'),
    instagram: t('instagram'),
    x: t('x'),
    rightsMessage: t('rights-message'),
    ministry: t('ministry'),
    volunteers: t('volunteering'),
    podcast: t('podcast'),
    feedback: t('feedback'),
    contact: t('contact')
  }

  return (
    <>
      <div ref={footerRef} className={styles.footer}>
        <div className={styles.innerFooter}>
          <div className={styles.columnOne}>
            <Link className={styles.upperCell} href='/feed'>
              <Image
                className={styles.footerLogo}
                src={'/img/sslm-dark.svg'}
                alt={variables.logoAlt}
                width={140.7084}
                height={55.6354}
              />
            </Link>
          </div>
          <div className={styles.columnTwo}>
            <div className={styles.columnFooter}>
              <div className={styles.column}>
                <h5>{variables.platform}</h5>
                <Link scroll className={styles.link} href={'/'}>{variables.home}</Link>
                <span>&bull;</span>
                <Link scroll className={styles.link} href={'/about'}>{variables.about}</Link>
                <span>&bull;</span>
                {/* <Link scroll className={styles.link} href={'/topics'}>{variables.explore}</Link> */}
                <Link scroll className={styles.link} href={'/podcast'}>{variables.podcast}</Link>
                <span>&bull;</span>
                <Link scroll className={styles.link} href={'/contact'}>{variables.contact}</Link>
                <span>&bull;</span>
                <Link scroll className={styles.link} href={'/donate'}>{variables.donate}</Link>
              </div>
              <div className={styles.column}>
                <h5>{variables.legal}</h5>
                <Link scroll className={styles.link} href={'/policies/privacy-policy'}>{variables.privacyPolicy}</Link>
                <span>&bull;</span>
                <Link scroll className={styles.link} href={'/policies/terms-of-service'}>{variables.termsOfService}</Link>
                <span>&bull;</span>
                <Link scroll className={styles.link} href={'/policies/community-guidelines'}>{variables.communityGuidelines}</Link>
                <span>&bull;</span>
                <Link scroll className={styles.link} href={'/policies/statement-of-faith'}>{variables.statementOfFaith}</Link>
                <span>&bull;</span>
                <Link scroll className={styles.link} href={'/help-center/donations'}>{variables.donationFAQ}</Link>
              </div>
              {/* <div className={styles.column}>
                <h5>{t('partners')}</h5>
                <Link scroll className={styles.link} href={'/volunteers'}>{variables.volunteers}</Link>
                <Link scroll className={styles.link} href={'/partnership-opportunities'}>{t('partnerships')}</Link>
                <Link scroll className={styles.link} href={'/content-suggestions'}>{variables.contentSuggestions}</Link>
              </div> */}
              {!user &&
                <div className={styles.column}>
                  <h5>{variables.account}</h5>
                  <Link scroll className={styles.link} href={'/v1/users/signup'}>{variables.signUp}</Link>
                  <span>&bull;</span>
                  <Link scroll className={styles.link} href={'/v1/users/login'}>{variables.logIn}</Link>
                </div>
              }
              <div className={styles.column}>
                <h5>{t('social')}</h5>
                <Link target='_blank' className={styles.link} href={'https://www.facebook.com/steppingstones.intl'}>{variables.facebook}</Link>
                <span>&bull;</span>
                <Link target='_blank' className={styles.link} href={'https://www.instagram.com/steppingstones.intl'}>{variables.instagram}</Link>
                <span>&bull;</span>
                <Link target='_blank' className={styles.link} href={'https://x.com/ssiministry'}>{variables.x}</Link>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.lowerCell}>
          {
            String(variables.rightsMessage || '')
              .replace('YYYY', String(new Date().getFullYear()))
              .replace('NNNN', variables.ministry || '')
          }
        </div>
      </div>
    </>
  )
}

export default MainFooter
